import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Wrench, SealCheck, Folders, Siren, WarningCircle, Sparkle, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_META = {
  wartung: { tone: "border-l-blue-500 bg-blue-500/5", icon: Wrench, label: "Wartung" },
  pruefung: { tone: "border-l-emerald-500 bg-emerald-500/5", icon: SealCheck, label: "Prüfung" },
  service: { tone: "border-l-violet-500 bg-violet-500/5", icon: Folders, label: "Service" },
  notdienst: { tone: "border-l-red-500 bg-red-500/5", icon: Siren, label: "Notdienst" },
};

function AppointmentChip({ a, onDragStart }) {
  const m = TYPE_META[a.type] || TYPE_META.wartung;
  const Icon = m.icon;
  return (
    <div draggable onDragStart={(e) => onDragStart(e, a)} data-testid={`appt-${a.id}`}
      className={`group cursor-grab rounded-md border border-l-4 ${m.tone} p-2.5 transition-all hover:shadow-sm active:cursor-grabbing ${a.conflict ? "ring-2 ring-red-500/60" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <Icon size={15} weight="duotone" className="shrink-0" />
          <span className="truncate text-xs font-semibold">{a.title}</span>
        </div>
        {a.conflict && <WarningCircle size={16} weight="fill" className="shrink-0 text-red-500" title="Konflikt: Doppelbelegung" />}
      </div>
      <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>{a.start?.slice(11, 16) || "—"}</span>
        <span className="uppercase tracking-wider">{a.status}</span>
      </div>
    </div>
  );
}

export default function Plantafel() {
  const [board, setBoard] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [rangeDays, setRangeDays] = useState("1");
  const [typ, setTyp] = useState("alle");
  const [dragged, setDragged] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams({ date, range_days: rangeDays });
    if (typ !== "alle") p.set("typ", typ);
    api.get(`/plan/board?${p.toString()}`).then((r) => setBoard(r.data));
  }, [date, rangeDays, typ]);

  useEffect(() => { load(); }, [load]);

  const onDragStart = (e, a) => { setDragged(a); e.dataTransfer.effectAllowed = "move"; };
  const onDrop = async (monteurName) => {
    if (!dragged || dragged.assignee === monteurName) { setDragged(null); return; }
    try {
      await api.post("/plan/assign", { appointment_id: dragged.id, assignee: monteurName });
      toast.success("Termin zugewiesen", { description: `${dragged.title} → ${monteurName}` });
      setDragged(null); load();
    } catch { toast.error("Zuweisung fehlgeschlagen"); }
  };

  if (!board) return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-96 rounded-md" />)}
    </div>
  );

  return (
    <div>
      <PageHeader title="Plantafel · APP-PLAN" subtitle="Einsatzplanung · orchestriert Kalender (D-14), Regeln (D-24) & Aufträge · reiner UI-Baustein">
        {board.suggested_monteur && (
          <Badge variant="outline" className="gap-1.5 rounded-md border-primary/30 bg-primary/10 text-primary" data-testid="plan-suggestion">
            <Sparkle size={14} weight="fill" /> Vorschlag: {board.suggested_monteur}
          </Badge>
        )}
      </PageHeader>

      <Card className="mb-6 flex flex-wrap items-end gap-3 rounded-md border p-4">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Datum</label>
          <input data-testid="plan-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Ansicht</label>
          <Select value={rangeDays} onValueChange={setRangeDays}>
            <SelectTrigger data-testid="plan-range" className="h-9 w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Tag</SelectItem>
              <SelectItem value="7">Woche</SelectItem>
              <SelectItem value="30">Monat</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Auftragsart</label>
          <Select value={typ} onValueChange={setTyp}>
            <SelectTrigger data-testid="plan-typ" className="h-9 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle Arten</SelectItem>
              <SelectItem value="wartung">Wartung</SelectItem>
              <SelectItem value="pruefung">Prüfung</SelectItem>
              <SelectItem value="service">Service</SelectItem>
              <SelectItem value="notdienst">Notdienst</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {board.conflicts?.length > 0 && (
          <Badge variant="outline" className="gap-1.5 rounded-md border-red-500/40 bg-red-500/10 text-red-600" data-testid="plan-conflicts">
            <WarningCircle size={14} weight="fill" /> {board.conflicts.length} Konflikt(e)
          </Badge>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(null)}
          data-testid="plan-backlog" className="rounded-md border border-dashed bg-muted/30 p-3">
          <div className="mb-3 flex items-center justify-between">
            <Label2>Backlog · nicht zugewiesen</Label2>
            <Badge variant="outline" className="rounded-md">{board.backlog.length}</Badge>
          </div>
          <div className="flex flex-col gap-2">
            {board.backlog.map((a) => <AppointmentChip key={a.id} a={a} onDragStart={onDragStart} />)}
            {board.backlog.length === 0 && <p className="py-6 text-center text-xs text-muted-foreground">Alles verplant.</p>}
          </div>
        </div>

        {board.columns.map((c) => (
          <div key={c.monteur_id} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(c.monteur)}
            data-testid={`plan-col-${c.monteur_id}`}
            className="rounded-md border p-3 transition-colors hover:border-primary/40">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <UserCircle size={18} weight="duotone" className="text-primary" />
                <span className="text-sm font-bold">{c.monteur}</span>
              </div>
              <div className="flex items-center gap-1.5">
                {c.conflicts > 0 && <Badge variant="outline" className="rounded-md border-red-500/40 bg-red-500/10 text-red-600">{c.conflicts}⚠</Badge>}
                <Badge variant="outline" className="rounded-md">{c.load}</Badge>
              </div>
            </div>
            <div className="flex min-h-[80px] flex-col gap-2">
              {c.appointments.map((a) => <AppointmentChip key={a.id} a={a} onDragStart={onDragStart} />)}
              {c.appointments.length === 0 && <p className="py-6 text-center text-xs text-muted-foreground">Termin hierher ziehen</p>}
            </div>
          </div>
        ))}
        {board.columns.length === 0 && (
          <p className="col-span-3 py-10 text-center text-sm text-muted-foreground">Keine Monteure gefunden. Lege Monteure in der Verwaltung an.</p>
        )}
      </div>
    </div>
  );
}
